/**
 * Authenticated admin API helpers (Bearer via `@/lib/db` `apiClient`).
 * Server routes are registered as admin-only (`AdminEndpointRegistry` + `adminMiddleware`).
 */

import { apiClient } from '@/lib/db';

// --- Server / cache ---

export async function postFlushCache(body?: { mirror?: boolean }): Promise<{ success: boolean; message: string }> {
    // `apiClient` sets `Content-Type: application/json`; an empty body is invalid JSON and OpenAPI validation returns 400.
    return apiClient<{ success: boolean; message: string }>('/api/flush-cache', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
    });
}

export async function postAdminSystemRestart(): Promise<{ message: string; pid: number }> {
    return apiClient<{ message: string; pid: number }>('/api/admin/system/restart', {
        method: 'POST',
        body: JSON.stringify({}),
    });
}

export async function getAdminSystem(): Promise<unknown> {
    return apiClient('/api/admin/system');
}

// --- Bans ---

export type AdminBanList = {
    bannedIPs: string[];
    bannedUserIds: string[];
    bannedTokens: string[];
};

export async function getAdminBanList(): Promise<AdminBanList> {
    return apiClient<AdminBanList>('/api/admin/bans');
}

export async function postAdminBanIps(ips: string[]): Promise<{ banned: number; skipped: number; message: string }> {
    return apiClient<{ banned: number; skipped: number; message: string }>('/api/admin/bans/ban-ip', {
        method: 'POST',
        body: JSON.stringify({ ips }),
    });
}

export async function postAdminUnbanIp(ip: string): Promise<{ success: boolean; message: string }> {
    return apiClient<{ success: boolean; message: string }>('/api/admin/bans/unban-ip', {
        method: 'POST',
        body: JSON.stringify({ ip }),
    });
}

export async function postAdminUnbanUser(userId: string): Promise<{ success: boolean; message: string }> {
    return apiClient<{ success: boolean; message: string }>('/api/admin/bans/unban-user', {
        method: 'POST',
        body: JSON.stringify({ userId }),
    });
}

export type AdminViolationStats = {
    totalViolations: number;
    violations: Array<{
        key: string;
        count: number;
        firstViolation: number;
        lastViolation: number;
    }>;
};

export async function getAdminViolationStats(): Promise<AdminViolationStats> {
    return apiClient<AdminViolationStats>('/api/admin/bans/violations');
}

// --- User management (admin) — `/api/admin/users` ---

export const fetchAdminUsersAPI = async (): Promise<unknown[]> => {
    return apiClient<unknown[]>('/api/admin/users');
};

export const createAdminUserAPI = async (
    email: string,
    password: string,
    copySettings: boolean,
): Promise<unknown> => {
    return apiClient('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email, password, copySettings }),
    });
};

export const updateAdminUserAPI = async (
    userId: string,
    profile: {
        username?: string | null;
        display_name?: string | null;
        bio?: string | null;
    },
): Promise<unknown> => {
    return apiClient(`/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body: JSON.stringify(profile),
    });
};

export const deleteAdminUserAPI = async (userId: string): Promise<void> => {
    await apiClient(`/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
    });
};
